import { LitElement, TemplateResult, css, html } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';

import { isDefined } from '../helpers/utils';
import { storeReferralFromUrl } from '../helpers/api/referral-api';

@customElement('referral-redirect-page')
export class ReferralRedirectPage extends LitElement {
    @property({ type: String })
    public referralCode: string = '';

    @state()
    private isRedirecting: boolean = false;

    @state()
    private redirectCountdown: number = 2;

    private countdownInterval?: number;

    async connectedCallback(): Promise<void> {
        super.connectedCallback();
        await this.handleReferralCapture();
    }

    disconnectedCallback(): void {
        super.disconnectedCallback();
        if (isDefined(this.countdownInterval)) {
            clearInterval(this.countdownInterval);
        }
    }

    private async handleReferralCapture(): Promise<void> {
        try {
            // Extract referral code from URL parameters for display
            const urlParams: URLSearchParams = new URLSearchParams(window.location.search);
            const referralCodeParam: string = urlParams.get('ref') ?? urlParams.get('referral') ?? '';

            if (referralCodeParam) {
                this.referralCode = referralCodeParam.trim();
            }

            // Use your existing referral capture function to store it and track the visit
            await storeReferralFromUrl(window.location.href);

        } catch (error) {
            console.warn('Failed to handle referral capture:', error);
        }
        window.dispatchEvent(new CustomEvent('page-content-ready'));

        // Start countdown and redirect
        this.startRedirectCountdown();
    }

    private startRedirectCountdown(): void {
        this.isRedirecting = true;

        this.countdownInterval = window.setInterval(() => {
            this.redirectCountdown -= 1;

            if (this.redirectCountdown <= 0) {
                this.redirectToMarketing();
            }
        }, 1000);
    }

    private redirectToMarketing(): void {
        if (isDefined(this.countdownInterval)) {
            clearInterval(this.countdownInterval);
        }

        // Redirect to marketing page
        const marketingUrl: string = 'https://proxi.co';
        const utmParams: URLSearchParams = new URLSearchParams({
            utm_source: 'referral',
            utm_medium: 'link',
            utm_campaign: 'user_referral',
        });

        if (this.referralCode) {
            utmParams.set('utm_content', this.referralCode);
        }

        const finalUrl: string = `${marketingUrl}?${utmParams.toString()}`;
        window.location.href = finalUrl;
    }

    private handleContinueClick(): void {
        this.redirectToMarketing();
    }

    render(): TemplateResult {
        return html`
            <div class="container">
                <div class="logo">Proxi</div>

                ${this.referralCode ? html`
                    <div class="message">
                        Thanks for using referral code:
                        <div class="referral-code">${this.referralCode}</div>
                        We've saved this for when you sign up!
                    </div>
                ` : html`
                    <div class="message">
                        Welcome to Proxi!
                        <br>Taking you to our main site...
                    </div>
                `}

                ${this.isRedirecting ? html`
                    <div class="countdown">
                        Redirecting in ${this.redirectCountdown} seconds...
                    </div>
                    <div class="spinner"></div>

                    <button
                        class="continue-button"
                        @click="${this.handleContinueClick}"
                    >
                        Continue Now
                    </button>
                ` : ''}
            </div>
        `;
    }


    static styles = css`
        :host {
            display: flex;
            flex-direction: column;
            justify-content: center;
            align-items: center;
            min-height: 100vh;
            padding: 2rem;
            box-sizing: border-box;
            background-color: #F7F7F7;
        }

        .container {
            max-width: 500px;
            width: 100%;
            background: var(--theme-white-color);
            border-radius: 12px;
            padding: 2.5rem 2rem;
            box-shadow: 0px 4px 6px 0 #ECECEC;
            text-align: center;
            border: 1px solid var(--theme-border-color, #ECECEC);
        }

        .logo {
            font-size: 2rem;
            font-weight: 700;
            margin-bottom: 1.5rem;
            color: var(--main-primary-color, #091E2C);
        }

        .message {
            font-size: 1.1rem;
            line-height: 1.5;
            margin-bottom: 2rem;
            color: var(--theme-input-color, #1D1B20);
        }

        .referral-code {
            font-family: 'SF Mono', 'Monaco', 'Consolas', monospace;
            background-color: #F7F7F7;
            padding: 0.75rem 1rem;
            border-radius: 8px;
            font-size: 1rem;
            font-weight: 600;
            margin: 1rem 0;
            border: 1px solid var(--theme-border-color, #ECECEC);
            color: var(--main-invalid-color, #E53E3E);
            word-break: break-all;
        }

        .countdown {
            font-size: 1.2rem;
            font-weight: 600;
            margin: 1.5rem 0;
            color: var(--theme-input-color, #1D1B20);
        }

        .spinner {
            border: 3px solid #ECECEC;
            border-radius: 50%;
            border-top: 3px solid var(--main-primary-color, #091E2C);
            width: 32px;
            height: 32px;
            animation: spin 1s linear infinite;
            margin: 1rem auto;
        }

        .continue-button {
            background: var(--main-primary-color, #091E2C);
            color: white;
            border: none;
            padding: 0.75rem 1.5rem;
            border-radius: 8px;
            font-size: 0.9rem;
            font-weight: 600;
            cursor: pointer;
            transition: all 0.2s ease;
            margin-top: 1rem;
            min-width: 120px;
        }

        .continue-button:hover {
            background: var(--main-secondary-color, #1A365D);
            transform: translateY(-1px);
        }

        .continue-button:active {
            transform: translateY(0);
        }

        @keyframes spin {
            0% { transform: rotate(0deg); }
            100% { transform: rotate(360deg); }
        }

        @media (max-width: 768px) {
            :host {
                padding: 1rem;
            }

            .container {
                padding: 2rem 1.5rem;
            }

            .logo {
                font-size: 1.75rem;
            }

            .message {
                font-size: 1rem;
            }

            .referral-code {
                font-size: 0.9rem;
            }
        }
    `;
}